import { useEffect } from "react";
import { Outlet, useNavigate, useLocation, useParams } from "react-router";
import { Button, Popconfirm, Tooltip, message } from "antd";
import { CheckCircleOutlined, CloudUploadOutlined } from "@ant-design/icons";
import { useTrialPlans } from "@/hooks/useTrialPlans";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import { SIMPLIFIED_ENTRY_STEP, isReachablePlanningStep } from "./steps";

const PHASE_COLORS: Record<string, { bg: string; text: string }> = {
  "Phase 1": { bg: "#D8EEF8", text: "#196A9F" },
  "Phase 1/2": { bg: "#D8EEF8", text: "#196A9F" },
  "Phase 2": { bg: "#E0DEF0", text: "#59528F" },
  "Phase 2/3": { bg: "#F8EEE1", text: "#C06119" },
  "Phase 3": { bg: "#F8EEE1", text: "#C06119" },
  "Phase 4": { bg: "#E7D2DF", text: "#8C2760" },
};

export default function TrialPlanningDetail() {
  const { id, planId } = useParams<{ id: string; planId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { getPlan, setScenarioApproval } = useTrialPlans();
  const simplified = useFeatureFlag(FEATURE_FLAGS.PLANNING_SIMPLIFIED, true);

  const plan = planId ? getPlan(planId) : undefined;

  // Without the plan Overview step, opening a plan lands on the first step of
  // the flow instead.
  const entryStep = simplified ? SIMPLIFIED_ENTRY_STEP : "home";

  // This route is the parent of every step, so it is also where a step the flow
  // no longer has gets turned away — removing the nav links leaves the URLs
  // reachable by bookmark, shared link or the back stack. A removed step is not
  // missing, though: the scenario simply does not have that stage, so the user
  // lands on its first step rather than on a not-found page outside the shell.
  useEffect(() => {
    const pathEnd = location.pathname.split("/").at(-1);
    const atPlanRoot = pathEnd === planId;
    const unreachable =
      !atPlanRoot && !isReachablePlanningStep(pathEnd ?? "", simplified);

    if (atPlanRoot || unreachable) {
      navigate(`/planning/${id}/${planId}/${entryStep}`, { replace: true });
    }
  }, [location.pathname, planId, id, navigate, entryStep, simplified]);

  if (!plan) {
    return (
      <div className="p-6 text-[#6B7280] text-[14px]">Plan not found.</div>
    );
  }

  const phaseStyle = plan.phase
    ? (PHASE_COLORS[plan.phase] ?? { bg: "#F3F4F6", text: "#374151" })
    : null;
  const milestonesSet = plan.milestones.filter((m) => m.start_date).length;
  const scenario = plan.scenarioSummaries.find(
    (sc) => sc.id === plan.activeScenarioId,
  );
  const approved = Boolean(scenario?.approvedAt);

  return (
    <div className="flex flex-col overflow-hidden" style={{ height: "100vh" }}>
      {/* Top bar — mirrors the trial header bar */}
      <div
        className="shrink-0 bg-white border-b border-[#E5E7EB] px-8 py-0 flex items-center"
        style={{ minHeight: 60 }}
      >
        {/* Approval state of the open scenario. Simplification replaces
            approval with a plain Save, so there is no state to report. */}
        {!simplified && (
          <span
            className={`mr-4 shrink-0 inline-flex items-center h-6 rounded-[4px] px-2.5 text-[12px] font-semibold ${
              approved
                ? "bg-[#DCF5EA] text-[#0B7E57]"
                : "bg-[#F3F4F6] text-[#6B7280]"
            }`}
          >
            {approved ? "Approved" : "Draft"}
          </span>
        )}

        {/* Plan name + meta */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-[17px] font-bold text-[#1F2933] m-0 leading-tight">
              {plan.name}
              <span
                className="font-normal"
                style={{ color: "#6B7280" }}
                title="Open scenario — manage on the Scenarios page"
              >
                {" "}
                — {scenario?.name ?? "Base plan"}
              </span>
            </h1>
            {phaseStyle && (
              <span
                className="inline-flex items-center h-5 rounded-[4px] px-2 text-[12px] font-semibold"
                style={{ background: phaseStyle.bg, color: phaseStyle.text }}
              >
                {plan.phase}
              </span>
            )}
            {plan.indication && (
              <span className="text-[14px] text-[#6B7280]">
                {plan.indication}
              </span>
            )}
          </div>
          <p className="text-[12px] text-[#6B7280] mt-0.5 m-0 leading-snug">
            {plan.sites.length} site{plan.sites.length !== 1 ? "s" : ""} ·{" "}
            {milestonesSet}/{plan.milestones.length} milestones set · Updated{" "}
            {new Date(plan.updatedAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </p>
        </div>

        {/* Approval + publish — Add to Vivo activates on approval but the
            publish flow itself is intentionally parked for now.
            Under PLANNING_SIMPLIFIED approval is just a save: no warning about
            locking the scenario, and no publish step at all. */}
        <div className="ml-4 shrink-0 flex items-center gap-2">
          {simplified ? (
            <Button
              icon={<CheckCircleOutlined />}
              onClick={() => {
                if (scenario) {
                  setScenarioApproval(plan.id, scenario.id, true);
                  message.success(`"${scenario.name}" saved`);
                }
              }}
            >
              Save
            </Button>
          ) : approved ? (
            <Popconfirm
              title="Set this scenario back to draft?"
              okText="Unapprove"
              cancelText="Cancel"
              onConfirm={() => {
                if (scenario) {
                  setScenarioApproval(plan.id, scenario.id, false);
                  message.success(`"${scenario.name}" back to draft`);
                }
              }}
            >
              <Button icon={<CheckCircleOutlined />}>Approved</Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title="Approve this scenario?"
              description="Approval marks the open scenario ready to publish. It should not be edited afterwards."
              okText="Approve"
              cancelText="Cancel"
              onConfirm={() => {
                if (scenario) {
                  setScenarioApproval(plan.id, scenario.id, true);
                  message.success(`"${scenario.name}" approved`);
                }
              }}
            >
              <Button icon={<CheckCircleOutlined />}>Approve Scenario</Button>
            </Popconfirm>
          )}
          {simplified ? null : approved ? (
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              className="bg-[#0C98D0] border-none"
            >
              Add to Vivo
            </Button>
          ) : (
            <Tooltip title="Approve the open scenario to enable publishing">
              <Button icon={<CloudUploadOutlined />} disabled>
                Add to Vivo
              </Button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Page content fills the remaining viewport, and scrolls when a page
          (e.g. Milestones, whose Gantt sits below the table) is taller than it. */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
        <Outlet />
      </div>
    </div>
  );
}
